import {
  Phase,
  TradeToEarnCampaign,
  TradeToEarnCampaignEpoch,
  TradeToEarnCampaignSummary,
  TradeToEarnCampaignWithStats,
  CampaignMarketType,
  CampaignTradingMode,
} from "@cede/types";
import { CEDE_CEX_LABELS, CEDE_CEXS, getNextEpoch, isDexId } from "@cede/utils";
import {
  CAMPAIGN_STATUS,
  CampaignStatusType,
  CampaignSummaryWithStats,
  CampaignWithStats,
  SortOptions,
  SortOptionType,
  SUPPORTED_HUB_EXCHANGES,
  ParticipationFilter,
  RewardViewType,
  RewardView,
} from "@/components/features/campaigns/types";
import { CAMPAIGN_RULE_FILTER_MAPPING } from "@/constants/trading";
import { isPerpsExchange } from "@/hooks/cex-perps";
import { format, compareDesc, compareAsc } from "date-fns";

/**
 * Get the current epoch for live campaigns, or the next epoch for future campaigns
 */
export const getCurrentOrStartingEpoch = (campaign: CampaignWithStats | CampaignSummaryWithStats) => {
  let currentEpoch = campaign.currentEpoch;

  if (campaign.status === CAMPAIGN_STATUS.FUTURE && !currentEpoch && campaign.nextEpoch) {
    currentEpoch = campaign.nextEpoch;
  }

  return currentEpoch;
};

export const getCampaignWithStatus = (campaign: TradeToEarnCampaignWithStats): CampaignWithStats => {
  return {
    ...campaign,
    isParticipating: false,
    currentVolume: 0,
    totalVolume: 0,
    participantCount: 0,
  };
};

export function getCurrentEpoch(campaignMetadata: TradeToEarnCampaign | null) {
  const now = new Date();
  const intervals = campaignMetadata?.epochs || [];

  return intervals.find((interval) => {
    const { start, end } = interval;
    return start && end && now >= new Date(start) && now < new Date(end);
  });
}

export function getCurrentEpochIndex(epochs: TradeToEarnCampaignEpoch[]) {
  const now = new Date();

  for (let i = 0; i < epochs.length; i++) {
    const start = epochs[i]?.start;
    const end = epochs[i]?.end;
    if (start && end && now >= new Date(start) && now < new Date(end)) {
      return i;
    }
  }

  if (now > new Date(epochs[epochs.length - 1]?.end ?? "")) return -2; // Competition is over

  return -1; // Default to first interval if none match
}

export const transformCampaigns = (
  campaigns: TradeToEarnCampaignSummary[],
  participatingCampaigns?: string[],
  campaignsStats?: Record<string, { currentVolume: number; totalVolume: number; participantCount: number }>,
): CampaignSummaryWithStats[] => {
  return campaigns.map((campaign) => ({
    ...campaign,
    isParticipating: participatingCampaigns ? participatingCampaigns.includes(campaign.name || "") : false,
    currentVolume: campaignsStats?.[campaign.name || ""]?.currentVolume || 0,
    totalVolume: campaignsStats?.[campaign.name || ""]?.totalVolume || 0,
    participantCount: campaignsStats?.[campaign.name || ""]?.participantCount || 0,
  }));
};

export const getPrizeNumber = (value?: string | null): number => {
  if (!value) return 0;
  // Extract the leading numeric portion, robust to any trailing token-symbol
  // ("6 USDT", "1.5 WETH", "100 mUSDT", "0.05 USD₮0" — symbols can contain
  // non-ASCII glyphs and digits, so stripping a trailing [a-z] is not enough).
  const match = value.replace(/[$,\s]/g, "").match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) || 0 : 0;
};

const hasPriorityNewBadge = (campaign: CampaignWithStats | CampaignSummaryWithStats) => {
  return campaign.isNew && campaign.status !== CAMPAIGN_STATUS.PAST;
};

/**
 * Sort NEW campaigns to the top of the list
 * - NEW campaigns are always shown at the top
 * - Sort order for multiple NEW campaigns: higher daily reward first, then lower campaign ID
 * - Non-NEW campaigns maintain their relative order
 */
export const sortWithNewCampaignsFirst = <T extends CampaignWithStats | CampaignSummaryWithStats>(
  campaigns: T[],
): T[] => {
  return [...campaigns].sort((a, b) => {
    const aIsNew = hasPriorityNewBadge(a);
    const bIsNew = hasPriorityNewBadge(b);

    // Both are NEW campaigns - sort by epoch reward (desc), then by ID (asc)
    if (aIsNew && bIsNew) {
      const aReward = getPrizeNumber(a.currentEpochReward);
      const bReward = getPrizeNumber(b.currentEpochReward);

      if (aReward !== bReward) {
        return bReward - aReward; // Higher reward first
      }

      // If rewards are tied, sort by campaign ID (ascending)
      const aId = Number(a.id) || 0;
      const bId = Number(b.id) || 0;
      return aId - bId;
    }

    // A is NEW, B is not - A comes first
    if (aIsNew && !bIsNew) return -1;

    // B is NEW, A is not - B comes first
    if (!aIsNew && bIsNew) return 1;

    // Neither is NEW - maintain existing order
    return 0;
  });
};

export const sortWithPrivateCampaignsFirst = <T extends CampaignWithStats | CampaignSummaryWithStats>(
  campaigns: T[],
): T[] => {
  return [...campaigns].sort((a, b) => {
    // NEW campaigns should not be affected by private campaign sorting
    const aIsNew = hasPriorityNewBadge(a);
    const bIsNew = hasPriorityNewBadge(b);

    if (aIsNew || bIsNew) return 0; // Preserve NEW campaign ordering

    if (
      a.privateCampaignPartner &&
      (a.status === CAMPAIGN_STATUS.LIVE || a.status === CAMPAIGN_STATUS.FUTURE) &&
      !b.privateCampaignPartner
    )
      return -1;
    if (
      !a.privateCampaignPartner &&
      b.privateCampaignPartner &&
      (b.status === CAMPAIGN_STATUS.LIVE || b.status === CAMPAIGN_STATUS.FUTURE)
    )
      return 1;
    return 0;
  });
};

const statusOrder: Record<CampaignStatusType, number> = {
  [CAMPAIGN_STATUS.LIVE]: 0,
  [CAMPAIGN_STATUS.FUTURE]: 1,
  [CAMPAIGN_STATUS.PAUSED]: 2,
  [CAMPAIGN_STATUS.PAST]: 3,
};

export const sortCampaignsByOption = <T extends CampaignWithStats | CampaignSummaryWithStats>(
  campaigns: T[],
  sortBy: SortOptionType,
  rewardView: RewardViewType = RewardView.PER_EPOCH,
): T[] => {
  return [...campaigns].sort((a, b) => {
    // NEW campaigns and private campaigns should not be affected by other sorts - preserve their priority position
    const aIsNew = hasPriorityNewBadge(a);
    const bIsNew = hasPriorityNewBadge(b);
    const aIsPrivate =
      !!a.privateCampaignPartner && (a.status === CAMPAIGN_STATUS.LIVE || a.status === CAMPAIGN_STATUS.FUTURE);
    const bIsPrivate =
      !!b.privateCampaignPartner && (b.status === CAMPAIGN_STATUS.LIVE || b.status === CAMPAIGN_STATUS.FUTURE);

    if (aIsNew || bIsNew || aIsPrivate || bIsPrivate) return 0; // Preserve priority campaign ordering

    // Date sorting
    if (sortBy === SortOptions.DATE_DESC || sortBy === SortOptions.DATE_ASC) {
      return sortBy === SortOptions.DATE_DESC
        ? compareDesc(new Date(a.startedAt || new Date()), new Date(b.startedAt || new Date()))
        : compareAsc(new Date(a.startedAt || new Date()), new Date(b.startedAt || new Date()));
    }

    // Reward sorting
    if (sortBy === SortOptions.REWARD_DESC || sortBy === SortOptions.REWARD_ASC) {
      const aReward = getRewardValueForSorting(a, rewardView);
      const bReward = getRewardValueForSorting(b, rewardView);
      return sortBy === SortOptions.REWARD_DESC ? bReward - aReward : aReward - bReward;
    }

    // Volume sorting
    if (sortBy === SortOptions.VOLUME_DESC || sortBy === SortOptions.VOLUME_ASC) {
      const aVolume = a.currentVolume || 0;
      const bVolume = b.currentVolume || 0;
      return sortBy === SortOptions.VOLUME_DESC ? bVolume - aVolume : aVolume - bVolume;
    }

    // Status sorting
    if (sortBy === SortOptions.STATUS_DESC || sortBy === SortOptions.STATUS_ASC) {
      const aStatus = statusOrder[a.status || "paused"] ?? 999;
      const bStatus = statusOrder[b.status || "paused"] ?? 999;
      return sortBy === SortOptions.STATUS_DESC ? bStatus - aStatus : aStatus - bStatus;
    }

    // Campaign name sorting
    if (sortBy === SortOptions.CAMPAIGN_NAME_DESC || sortBy === SortOptions.CAMPAIGN_NAME_ASC) {
      const aName = a.name || "";
      const bName = b.name || "";
      return sortBy === SortOptions.CAMPAIGN_NAME_DESC ? bName.localeCompare(aName) : aName.localeCompare(bName);
    }

    // Epoch end time sorting (for campaigns with active epochs)
    if (sortBy === SortOptions.EPOCH_END_DESC || sortBy === SortOptions.EPOCH_END_ASC) {
      const getEpochEndTime = (campaign: CampaignWithStats | CampaignSummaryWithStats) => {
        const currentEpoch = campaign.currentEpoch;
        if (!currentEpoch?.end) return null;
        return new Date(currentEpoch.end).getTime();
      };

      const aEpochEnd = getEpochEndTime(a);
      const bEpochEnd = getEpochEndTime(b);

      // Campaigns without epochs go to the end
      if (aEpochEnd === null && bEpochEnd === null) return 0;
      if (aEpochEnd === null) return 1;
      if (bEpochEnd === null) return -1;

      // ASC: soonest ending first (smaller timestamp = sooner)
      // DESC: latest ending first (larger timestamp = later)
      return sortBy === SortOptions.EPOCH_END_ASC ? aEpochEnd - bEpochEnd : bEpochEnd - aEpochEnd;
    }

    // Default fallback to reward sorting (desc)
    const aReward = getRewardValueForSorting(a, rewardView);
    const bReward = getRewardValueForSorting(b, rewardView);
    return bReward - aReward;
  });
};

export const sortWithLivePriority = (
  campaigns: Array<CampaignWithStats | CampaignSummaryWithStats>,
): Array<CampaignWithStats | CampaignSummaryWithStats> => {
  // Separate priority campaigns (NEW and private) first
  const priorityCampaigns: Array<CampaignWithStats | CampaignSummaryWithStats> = [];
  const regularCampaigns: Array<CampaignWithStats | CampaignSummaryWithStats> = [];

  campaigns.forEach((campaign) => {
    const isPrivate =
      !!campaign.privateCampaignPartner &&
      (campaign.status === CAMPAIGN_STATUS.LIVE || campaign.status === CAMPAIGN_STATUS.FUTURE);
    if (hasPriorityNewBadge(campaign) || isPrivate) {
      priorityCampaigns.push(campaign);
    } else {
      regularCampaigns.push(campaign);
    }
  });

  // Sort regular campaigns by status priority
  const liveCampaigns: Array<CampaignWithStats | CampaignSummaryWithStats> = [];
  const futureCampaigns: Array<CampaignWithStats | CampaignSummaryWithStats> = [];
  const pausedCampaigns: Array<CampaignWithStats | CampaignSummaryWithStats> = [];
  const otherCampaigns: Array<CampaignWithStats | CampaignSummaryWithStats> = [];

  regularCampaigns.forEach((campaign) => {
    if (campaign.status === CAMPAIGN_STATUS.LIVE) liveCampaigns.push(campaign);
    else if (campaign.status === CAMPAIGN_STATUS.FUTURE) futureCampaigns.push(campaign);
    else if (campaign.status === CAMPAIGN_STATUS.PAUSED) pausedCampaigns.push(campaign);
    else otherCampaigns.push(campaign);
  });

  // Priority campaigns first (NEW and private), then regular campaigns by status priority
  return [...priorityCampaigns, ...liveCampaigns, ...futureCampaigns, ...pausedCampaigns, ...otherCampaigns];
};

export const filterCampaignsByParticipation = (
  campaign: CampaignWithStats | CampaignSummaryWithStats,
  participationFilter: string | null,
): boolean => {
  if (!participationFilter) {
    return true; // Show all campaigns when no filter is applied
  }

  switch (participationFilter) {
    case ParticipationFilter.PARTICIPATING:
      return campaign.isParticipating;
    case ParticipationFilter.NOT_PARTICIPATING:
      return !campaign.isParticipating;
    default:
      return true;
  }
};

export const formatEpochTime = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const hours = date.getUTCHours();

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${displayHours}${period} UTC`;
};

export const getSupportedHubExchangeIds = () => {
  return Object.values(SUPPORTED_HUB_EXCHANGES);
};

export const formatCampaignDate = (startDate?: string | Date, endDate?: string | Date): string => {
  if (!startDate || !endDate) return "";
  return `${format(new Date(startDate), "do MMMM")} — ${format(new Date(endDate), "do MMMM")}`;
};

export const getCurrentPhase = (phases: Phase[]) => {
  const now = new Date();

  for (const phase of phases) {
    if (now >= phase.start && now < phase.end) {
      return phase;
    }
  }

  return null;
};

// Table utility functions
export const createRuleFilterOptions = (selectedRules: string[] = []) => {
  return Object.keys(CAMPAIGN_RULE_FILTER_MAPPING).map((displayName) => ({
    id: displayName,
    label: displayName,
    checked: selectedRules.includes(displayName),
  }));
};

export const calculateMinRowsPerPage = (activeCampaignsCount: number, defaultRows = 15) => {
  return Math.max(activeCampaignsCount + 3, defaultRows);
};

export const createRowsPerPageOptions = (minRows: number) => {
  const options = [minRows, 25, 30];
  return [...new Set(options)].sort((a, b) => a - b);
};

/**
 * Format exchange IDs to readable labels
 */
export const formatExchangeLabels = (exchangeIds: string[]): string => {
  return exchangeIds.map((id) => CEDE_CEX_LABELS[id] || id).join(", ");
};

/**
 * Check if a campaign is an onchain (DEX) campaign
 * A campaign is considered onchain if any of its exchangeIds is a DEX ID (contains pipe separator)
 * @param campaign - The campaign to check
 * @returns true if the campaign has at least one DEX exchange
 */
export const isOnchainCampaign = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): boolean => {
  if (!campaign) return false;

  const exchangeIds = campaign.exchangeIds;
  if (!exchangeIds || exchangeIds.length === 0) return false;

  return exchangeIds.some((exchangeId) => isDexId(exchangeId));
};

/**
 * Get the DEX ID from a campaign's exchangeIds
 * @param campaign - The campaign to check
 * @returns The first DEX ID found, or null if none
 */
export const getCampaignDexId = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): string | null => {
  if (!campaign) return null;

  const exchangeIds = campaign.exchangeIds;
  if (!exchangeIds || exchangeIds.length === 0) return null;

  return exchangeIds.find((exchangeId) => isDexId(exchangeId)) ?? null;
};

/**
 * Check if a campaign is a Hyperliquid campaign
 * Hyperliquid requires special handling: wallet-based auth, perps UI, leverage support
 * @param campaign - The campaign to check
 * @returns true if the campaign has Hyperliquid as an exchange
 */
export const isHyperliquidCampaign = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): boolean => {
  if (!campaign?.exchangeIds?.length) return false;
  return campaign.exchangeIds.includes(CEDE_CEXS.HYPERLIQUID);
};

/**
 * Check if a campaign is a Solana DEX campaign (Raydium, Meteora, Orca, Jupiter, etc.)
 * Solana campaigns require special handling: Solana wallet connection (desktop only for now)
 * @param campaign - The campaign to check
 * @returns true if the campaign has a Solana DEX exchange
 */
export const isSolanaCampaign = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): boolean => {
  if (!campaign?.exchangeIds?.length) return false;
  return campaign.exchangeIds.some((id) => id.includes("solana"));
};

/**
 * Check if a campaign rides the cex-perps lane (CEX perps like Kraken Futures, or perps DEXes
 * like Katana that share the BE-proxied surface). Hyperliquid is NOT included — it has its own
 * dedicated interface. Naming follows the existing `cex-perps` lane until the broader rename
 * to `proxied-perps` lands.
 * @param campaign - The campaign to check
 * @returns true if the campaign has a perps exchange handled by the cex-perps lane
 */
export const isCexPerpsCampaign = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): boolean => {
  if (!campaign?.exchangeIds?.length) return false;
  return campaign.marketType === CampaignMarketType.PERPS && campaign.exchangeIds.some((id) => isPerpsExchange(id));
};

/**
 * Get the cex-perps-lane exchange ID from a campaign (CEX perps or perps DEX like Katana).
 * @param campaign - The campaign to check
 * @returns The first perps exchange ID found, or null if none
 */
export const getCexPerpsExchangeId = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
) => {
  if (!campaign?.exchangeIds?.length) return null;
  return campaign.exchangeIds.find((id) => isPerpsExchange(id)) ?? null;
};

/**
 * Check if a campaign is a Polymarket prediction market campaign
 * Polymarket campaigns have predictionEventIds configured (event IDs)
 * @param campaign - The campaign to check
 * @returns true if the campaign has predictionEventIds configured
 */
export const isPolymarketCampaign = (
  campaign: TradeToEarnCampaign | CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): boolean => {
  if (!campaign) return false;
  return Array.isArray(campaign.predictionEventIds) && campaign.predictionEventIds.length > 0;
};

/**
 * Get the epoch duration in days for a campaign
 * Uses the current epoch's epochGroup to find the durationMs
 * @param campaign - The campaign to get epoch duration from
 * @returns The epoch duration in days, or null if not available
 */
export const getEpochDurationInDays = (
  campaign: CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): number | null => {
  if (!campaign) return null;

  const currentEpoch = getCurrentOrStartingEpoch(campaign);
  if (!currentEpoch) return null;

  // Find the epoch group that matches the current epoch
  const epochGroup = campaign.epochGroups?.find((group) => group.id === currentEpoch.epochGroupId);

  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  if (epochGroup?.durationMs) {
    return epochGroup.durationMs / MS_PER_DAY;
  }

  // Fallback for campaigns where epoch group duration is missing.
  if (currentEpoch.start && currentEpoch.end) {
    const durationMs = new Date(currentEpoch.end).getTime() - new Date(currentEpoch.start).getTime();
    if (durationMs > 0) {
      return durationMs / MS_PER_DAY;
    }
  }

  return null;
};

/**
 * Get the formatted epoch duration string using hours and minutes (e.g., "6h 0m", "24h 0m")
 * @param campaign - The campaign to get epoch duration from
 * @returns The formatted duration string, or null if not available
 */
export const getFormattedEpochDuration = (
  campaign: CampaignWithStats | CampaignSummaryWithStats | null | undefined,
): string | null => {
  if (!campaign) return null;

  const currentEpoch = getCurrentOrStartingEpoch(campaign);
  if (!currentEpoch) return null;

  // Find the epoch group that matches the current epoch
  const epochGroup = campaign.epochGroups?.find((group) => group.id === currentEpoch.epochGroupId);

  const MS_PER_MINUTE = 60 * 1000;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;

  const totalMs =
    epochGroup?.durationMs ??
    (currentEpoch.start && currentEpoch.end
      ? new Date(currentEpoch.end).getTime() - new Date(currentEpoch.start).getTime()
      : null);

  if (!totalMs || totalMs <= 0) return null;
  // Always use total hours (no days conversion)
  const totalHours = Math.floor(totalMs / MS_PER_HOUR);
  const minutes = Math.floor((totalMs % MS_PER_HOUR) / MS_PER_MINUTE);

  if (totalHours > 0) {
    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }
  if (minutes > 0) {
    const seconds = Math.floor((totalMs % MS_PER_MINUTE) / 1000);
    return `${minutes}m ${seconds}s`;
  }
  return "0s";
};

/**
 * Calculate the daily reward from the epoch reward
 * @param epochReward - The total reward for the epoch (string like "$1,000")
 * @param epochDurationDays - The duration of the epoch in days
 * @returns The daily reward as a number, or null if cannot be calculated
 */
export const calculateDailyReward = (
  epochReward: string | null | undefined,
  epochDurationDays: number | null,
): number | null => {
  if (!epochReward || !epochDurationDays || epochDurationDays <= 0) return null;

  const epochRewardNumber = getPrizeNumber(epochReward);
  if (epochRewardNumber < 0) return null;

  return epochRewardNumber / epochDurationDays;
};

/**
 * Get the reward value based on the selected view (daily or per epoch)
 * @param campaign - The campaign to get reward from
 * @param rewardView - The selected reward view type
 * @returns The formatted reward string, or null if not available
 */
export const getRewardByView = (
  campaign: CampaignWithStats | CampaignSummaryWithStats | null | undefined,
  rewardView: RewardViewType,
): string | null => {
  if (!campaign?.currentEpochReward) return null;

  if (rewardView === RewardView.PER_EPOCH) {
    // Strip any leading "$" to ensure consistent numeric format
    return campaign.currentEpochReward.replace(/^\$/, "");
  }

  // Calculate daily reward
  const epochDurationDays = getEpochDurationInDays(campaign);
  const dailyReward = calculateDailyReward(campaign.currentEpochReward, epochDurationDays);

  if (dailyReward === null) return null;

  // Format the daily reward
  if (dailyReward >= 1000) {
    return Math.round(dailyReward).toLocaleString();
  }
  return dailyReward.toFixed(dailyReward < 10 ? 2 : 0);
};

/**
 * Get the numeric reward value for sorting based on the selected view
 * @param campaign - The campaign to get reward from
 * @param rewardView - The selected reward view type
 * @returns The numeric reward value for sorting
 */
export const getRewardValueForSorting = (
  campaign: CampaignWithStats | CampaignSummaryWithStats | null | undefined,
  rewardView: RewardViewType,
): number => {
  if (!campaign?.currentEpochReward) return 0;

  if (rewardView === RewardView.PER_EPOCH) {
    return getPrizeNumber(campaign.currentEpochReward);
  }

  const epochDurationDays = getEpochDurationInDays(campaign);
  const dailyReward = calculateDailyReward(campaign.currentEpochReward, epochDurationDays);

  return dailyReward ?? 0;
};

/**
 * Resolve trading mode for the most-relevant epoch (current if running, else
 * the first upcoming one), with campaign-level fallback. Inheriting from the
 * upcoming epoch lets future / coming-soon campaigns expose the trading mode
 * they're configured to launch with (e.g. STREAK_ONLY) instead of silently
 * collapsing to MANUAL_ONLY before they start.
 *
 * Resolution order: epochGroup.tradingMode > campaign.tradingMode > MANUAL_ONLY
 */
export function resolveTradingMode(campaign: TradeToEarnCampaign | null | undefined): CampaignTradingMode {
  if (!campaign) return CampaignTradingMode.MANUAL_ONLY;
  const referenceEpoch = getCurrentEpoch(campaign) ?? getNextEpoch(campaign);
  const epochGroup =
    referenceEpoch?.epochGroupId != null
      ? campaign.epochGroups?.find((eg) => eg.id === referenceEpoch.epochGroupId)
      : undefined;
  return epochGroup?.tradingMode ?? campaign.tradingMode ?? CampaignTradingMode.MANUAL_ONLY;
}
